import {graphql, useStaticQuery} from 'gatsby'
import React, {Fragment} from 'react'
import Helmet from 'react-helmet'

type MetaArrayType = ConcatArray<{name: string; content: string} | {property: string; content: string}>

export interface SEOProps {
  description?: string
  lang?: 'en'
  meta?: MetaArrayType
  previewImage?: {path: string; width: string; height: string} | null
  title?: string
  useTitleTemplate?: boolean
}

export interface SiteQueryType {
  site: {
    siteMetadata: {
      title: string
      description: string
      url: string
      twitterUsername: string
      ogImage: string
      twitterImage: string
    }
  }
}

/**
 * Gatsby SEO component. Use this component to add metadata to a page.
 * NOTE: we also use this component to load scripts, and to add variables to the window.
 * ^ but we should probably move script + variable loading to its own component.
 * @returns Helmet components w/ site metadata + script tags for trackers
 */
function SEO({
  description = '',
  lang = 'en',
  meta = [],
  previewImage = null,
  title = '',
  useTitleTemplate = true,
}: SEOProps): JSX.Element {
  const {site} = useStaticQuery<SiteQueryType>(
    graphql`
      query {
        site {
          siteMetadata {
            title
            description
            url
            twitterUsername
            ogImage
            twitterImage
          }
        }
      }
    `,
  )

  const metaDescription: string = description || site.siteMetadata.description || ''
  const ogImagePath: string = previewImage ? previewImage.path : site.siteMetadata.ogImage
  const twitterImagePath: string = previewImage ? previewImage.path : site.siteMetadata.twitterImage
  const siteTitle: string = title || site.siteMetadata.title || ''

  return (
    <Fragment>
      <Helmet
        defaultTitle={site.siteMetadata.title}
        htmlAttributes={{lang}}
        meta={[
          {
            content: metaDescription,
            name: 'description',
          },
          {
            content: siteTitle,
            property: 'og:title',
          },

          {
            content: 'Culdesac',
            property: 'og:site_name',
          },
          {
            content: 'en_US',
            property: 'og:locale',
          },
          {
            content: metaDescription,
            property: 'og:description',
          },
          {
            content: 'website',
            property: 'og:type',
          },
          {
            content: 'https://culdesac.com/',
            property: 'og:url',
          },
          {
            content: `https://culdesac.com/${ogImagePath}`,
            property: 'og:image',
          },
          {
            content: previewImage ? previewImage.width : '1200',
            property: 'og:image:width',
          },
          {
            content: previewImage ? previewImage.height : '630',
            property: 'og:image:height',
          },
          {
            content: 'summary_large_image',
            name: 'twitter:card',
          },
          {
            content: siteTitle,
            name: 'twitter:title',
          },
          {
            content: site.siteMetadata.twitterUsername,
            name: 'twitter:site',
          },
          {
            content: metaDescription,
            name: 'twitter:description',
          },
          {
            content: `https://culdesac.com/${twitterImagePath}`,
            name: 'twitter:image',
          },
          {
            content: site.siteMetadata.twitterUsername,
            name: 'twitter:creator',
          },
        ].concat(meta)}
        title={title}
        titleTemplate={useTitleTemplate ? '%s | Culdesac' : undefined}
      />
    </Fragment>
  )
}

export default SEO
